"use client";

import * as React from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function useReducedMotion() {
  const [reduced, setReduced] = React.useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  React.useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return reduced;
}

function useVisibilityPause(ref: React.RefObject<Element | null>) {
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.05 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return visible;
}

export interface BorderBeamPanelProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  color?: string;
  active?: boolean;
  thickness?: number;
  idleSpeed?: number;
  hoverSpeed?: number;
  radius?: number;
}

export function BorderBeamPanel({
  children,
  color = "#5b6550",
  active = true,
  thickness = 2,
  idleSpeed = 10,
  hoverSpeed = 45,
  radius = 0,
  className,
  style,
  ...props
}: BorderBeamPanelProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const reducedMotion = useReducedMotion();
  const visible = useVisibilityPause(rootRef);
  const angle = React.useRef(0);
  const speed = React.useRef(idleSpeed);

  React.useEffect(() => {
    if (reducedMotion || !visible || !active) return;

    let animationFrame = 0;
    let previousTime = 0;
    const animate = (time: number) => {
      if (previousTime) {
        const delta = Math.min((time - previousTime) / 1000, 0.05);
        angle.current = (angle.current + speed.current * delta) % 360;
        rootRef.current?.style.setProperty("--beam-angle", `${angle.current}deg`);
      }
      previousTime = time;
      animationFrame = requestAnimationFrame(animate);
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [active, reducedMotion, visible]);

  return (
    <div
      ref={rootRef}
      onPointerEnter={() => {
        speed.current = hoverSpeed;
      }}
      onPointerLeave={() => {
        speed.current = idleSpeed;
      }}
      onFocus={() => {
        speed.current = hoverSpeed;
      }}
      onBlur={() => {
        speed.current = idleSpeed;
      }}
      className={cn(
        "relative isolate w-full overflow-hidden border bg-white dark:bg-neutral-950",
        className,
      )}
      style={{
        borderRadius: radius,
        ["--beam-angle" as string]: reducedMotion ? "40deg" : "0deg",
        ...style,
      }}
      {...props}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          padding: thickness,
          borderRadius: radius,
          opacity: active ? 1 : 0,
          transition: "opacity 180ms ease",
          background: `conic-gradient(from var(--beam-angle), transparent 0deg, transparent 276deg, color-mix(in srgb, ${color} 15%, transparent) 298deg, ${color} 322deg, color-mix(in srgb, ${color} 28%, transparent) 344deg, transparent 360deg)`,
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          maskComposite: "exclude",
        }}
      />
      {children}
    </div>
  );
}
